/**
 * `/impersonate/land` — the per-tab landing for "Access school/chef view".
 *
 * The impersonation callback (apps/ordo server) redirects here with the signed
 * session + banner tokens in the URL fragment (`#s=…&b=…`). The fragment never
 * reaches the server, keeping the tokens out of logs/history. This page stashes
 * them in sessionStorage — isolated to this tab and destroyed when the tab
 * closes — then refreshes auth (which re-sends them as headers) and lands the
 * operator in the app.
 *
 * Sits OUTSIDE the auth-protected routes: at this moment the tab has no session
 * yet; the fragment tokens ARE the credential.
 */

import { useEffect } from 'react'

import { IMPERSONATION_BANNER_STORAGE_KEY, IMPERSONATION_SESSION_STORAGE_KEY } from '@/api/http'
import { useAuthStore } from '@/stores/auth'

export function readFragmentToken(hash: string, key: string): string | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const value = params.get(key)
  return value && value.length > 0 ? value : null
}

export function ImpersonateLandPage() {
  useEffect(() => {
    const hash = window.location.hash
    const session = readFragmentToken(hash, 's')
    const banner = readFragmentToken(hash, 'b')

    if (session) {
      try {
        sessionStorage.setItem(IMPERSONATION_SESSION_STORAGE_KEY, session)
        if (banner) {
          sessionStorage.setItem(IMPERSONATION_BANNER_STORAGE_KEY, banner)
        }
      } catch {
        // ignore — storage may be unavailable (private mode, etc.)
      }
    }

    // Drop the tokens from the URL so a reload / shared link can't replay them,
    // then resolve the impersonated session and enter the app.
    window.history.replaceState(null, '', '/')
    void useAuthStore
      .getState()
      .refresh()
      .finally(() => {
        window.location.replace('/')
      })
  }, [])

  return null
}
